import { useState, useMemo, useEffect, useRef } from "preact/hooks";
import createUtilityCanvas from "@brickme/project-core/src/render/create-utility-canvas.ts";
import { useCurrentBrickedBuild } from "@brickme/frontend-common/src/picture-build/picture-build-context.tsx";
import useMeasure from "@brickme/frontend-common/src/hooks/use-measure.ts";
import createRenderer from "@brickme/frontend-common/src/picture-render/create-renderer.ts";
import type {
	RenderRunner,
	RenderSource,
} from "@brickme/frontend-common/src/picture-render/render-runner-type.ts";
import { usePicture } from "@brickme/frontend-common/src/picture-edit/picture-edit-context.tsx";
import { useRenderMode } from "@brickme/frontend-common/src/picture-render/render-mode-context.tsx";
import { createDrawSize } from "@brickme/frontend-common/src/picture-render/canvas-bounds.ts";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import { multiplySize } from "@brickme/project-core/src/model/geom.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import { ensureExhaustive } from "../utils/never.ts";

type Options = {
	readonly sourceImage: Bitmap;
	readonly drawBaseplates?: boolean;
};

function useCanvasRenderer({ sourceImage, drawBaseplates }: Options) {
	const bricked = useCurrentBrickedBuild();

	// Render worker
	const [renderer] = useState<RenderRunner<HTMLCanvasElement>>(createRenderer);
	useEffect(() => {
		return () => {
			renderer.terminate();
		};
	}, [renderer]);

	// Canvas
	const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
	const [setCanvasParentRef, { width: parentWidth, height: parentHeight }] =
		useMeasure<HTMLElement>();
	const brickedImage = bricked?.image;
	useEffect(() => {
		if (canvas === null) {
			return;
		}

		renderer.setCanvas(canvas);
	}, [canvas, renderer]);
	useEffect(() => {
		if (renderer) {
			renderer.setSourceImage(sourceImage, document.createElement("canvas"));
		}
	}, [sourceImage, renderer]);
	useEffect(() => {
		renderer.setDrawBaseplates(!!drawBaseplates);
	}, [drawBaseplates, renderer]);

	// Perform render
	const { picture } = usePicture();
	const { activeRenderMode } = useRenderMode();
	const renderSource = useMemo((): RenderSource => {
		if (brickedImage === undefined || activeRenderMode === "source") {
			return {
				type: "source",
			};
		}
		if (activeRenderMode === "bricked" || activeRenderMode === "solid") {
			return {
				type: activeRenderMode,
				build: brickedImage,
			};
		}
		ensureExhaustive(activeRenderMode);
	}, [activeRenderMode, brickedImage]);
	useEffect(() => {
		renderer.setPictureConfiguration(picture);
	}, [picture, renderer]);
	useEffect(() => {
		renderer.setSource(renderSource);
	}, [renderer, renderSource]);

	// Should be a quicker way to do this. Only want a new utility canvas when renderSource
	// leads to dimension changes
	const { buildSize, drawSize } = useMemo(() => {
		let buildSize: GenericSize<number>;
		switch (renderSource.type) {
			case "source":
				buildSize = multiplySize(
					picture.numberOfBasePlates,
					picture.basePlateSize,
				);
				break;
			case "solid":
			case "bricked":
				buildSize = {
					width: renderSource.build.width,
					height: renderSource.build.height,
				};
				break;
			default:
				ensureExhaustive(renderSource);
		}

		const drawSize = createDrawSize(
			buildSize,
			{
				width: parentWidth,
				height: parentHeight,
			},
			{ minTotalMargin: 20 },
		);
		return { drawSize, buildSize };
	}, [renderSource, parentWidth, parentHeight]);
	const sentUtilitySizes = useRef<{
		drawSize: GenericSize<number>;
		buildSize: GenericSize<number>;
	}>();
	useEffect(() => {
		const { current: currentSentUtilitySizes } = sentUtilitySizes;
		if (
			currentSentUtilitySizes === undefined ||
			currentSentUtilitySizes.buildSize.width !== buildSize.width ||
			currentSentUtilitySizes.buildSize.height !== buildSize.height ||
			currentSentUtilitySizes.drawSize.width !== drawSize.width ||
			currentSentUtilitySizes.drawSize.height !== drawSize.height
		) {
			const utilityCanvas = createUtilityCanvas(
				{ buildMap: buildSize, drawBounds: drawSize },
				(width, height) => {
					const canvas = document.createElement("canvas");
					canvas.width = width;
					canvas.height = height;
					return canvas;
				},
			);
			renderer.setUtilityCanvas(utilityCanvas, drawSize);
			sentUtilitySizes.current = {
				drawSize,
				buildSize,
			};
		}
	}, [buildSize, drawSize]);

	return {
		canvasRef: setCanvas,
		canvasParentRef: setCanvasParentRef,
		drawSize,
	};
}

export default useCanvasRenderer;
